import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUsers,
  getManagers,
  getAssistantSuperAdmin,
  addUser,
  updateUser,
  deleteUser,
  getStaffs,
  getUserById,
  getManagersByUser,
  getStaffsByUser,
} from "./userApi";

// Helper to catch errors properly
const handleApiError = (err) => {
  return err.response.data.msg || err.message || "Unknown error";
};

// Thunks with rejectWithValue for better error handling
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getUsers(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async (_id, { rejectWithValue }) => {
    try {
      const response = await getUserById(_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchManagers = createAsyncThunk(
  "users/managers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getManagers();
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchManagersByUser = createAsyncThunk(
  "users/createdByManagers",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getManagersByUser(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchStaffsByUser = createAsyncThunk(
  "users/createdByStaffs",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getStaffsByUser(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchAssistantSuperadmin = createAsyncThunk(
  "users/assistant-superadmin",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAssistantSuperAdmin();
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const fetchStaffs = createAsyncThunk(
  "users/staffs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getStaffs();
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const formUserData = new FormData();
      Object.entries(userData).forEach(([key, value]) => {
        if (value) formUserData.append(key, value);
      });
      const response = await addUser(formUserData);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const updateUserData = createAsyncThunk(
  "users/updateUser",
  async (userData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(userData).forEach(([key, value]) => {
        if (value instanceof File || typeof value === "string") {
          formData.append(key, value);
        }
      });
      const response = await updateUser(formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const deleteUserById = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteUser(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState: {
    users: [],
    usersCreatedBy: [],
    managers: [],
    staffs: [],
    assistantSuperAdmin: [],
    managersByUser: [],
    staffsByUser: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      // fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      // fetchUserById
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.usersCreatedBy = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch user by ID";
      })

      // fetchManagers
      .addCase(fetchManagers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagers.fulfilled, (state, action) => {
        state.loading = false;
        state.managers = action.payload;
      })
      .addCase(fetchManagers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch managers";
      })

      // fetchStaffs
      .addCase(fetchStaffs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffs.fulfilled, (state, action) => {
        state.loading = false;
        state.staffs = action.payload;
      })
      .addCase(fetchStaffs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch staffs";
      })

      // fetchAssistantSuperadmin
      .addCase(fetchAssistantSuperadmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssistantSuperadmin.fulfilled, (state, action) => {
        state.loading = false;
        state.assistantSuperAdmin = action.payload;
      })
      .addCase(fetchAssistantSuperadmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch assistant superadmin";
      })

      // fetchManagersByUser
      .addCase(fetchManagersByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagersByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.managersByUser = action.payload;
      })
      .addCase(fetchManagersByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch managers by user";
      })

      // fetchStaffsByUser
      .addCase(fetchStaffsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.staffsByUser = action.payload;
      })
      .addCase(fetchStaffsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch staffs by user";
      })

      // createUser
      .addCase(createUser.pending, (state) => {
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to create user";
      })

      // updateUserData
      .addCase(updateUserData.pending, (state) => {
        state.error = null;
      })
      .addCase(updateUserData.fulfilled, (state, action) => {
        const idx = state.users.findIndex((u) => u._id === action.payload._id);
        if (idx !== -1) {
          state.users[idx] = action.payload;
        }
      })
      .addCase(updateUserData.rejected, (state, action) => {
        state.error = action.payload || "Failed to update user";
      })

      // deleteUserById
      .addCase(deleteUserById.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteUserById.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete user";
      });
  },
});

export default userSlice.reducer;